// NOTE: Taken directly from WEB repo.
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { get, omit, isEmpty, isString } from 'lodash';

import LocationParser from '../../utils/locationParser';

import './LocationPicker.css';

/* global google */

const readableLocation = (locationObj) => {
  if (isString(locationObj)) return locationObj;

  const location = locationObj || {};
  let display = '';
  if (location.address) {
    display = location.address;
  }
  else {
    display = [
      get(location, 'locality.long'),
      get(location, 'region.long'),
      get(location, 'country.long'),
    ].filter(name => !isEmpty(name)).join(', ');
  }
  return display;
};


class Pill extends Component {
  static propTypes = {
    name: PropTypes.node.isRequired,
    editable: PropTypes.bool,
    onClick: PropTypes.func,
    className: PropTypes.string,
  }

  handleClick = () => {
    const { onClick, name } = this.props;
    if (onClick) this.props.onClick(name);
  }

  render() {
    const { name, editable, className, ...otherProps } = this.props;
    const classNames = ['pill'];
    if (editable) classNames.push('pill--editable');
    if (className) classNames.push(className);

    return (
      <div
        className={classNames.join(' ')}
        /*
          NOTE: otherProps is passed in to fix a bug where <Pill /> as a child of
          a Tooltip would not work (Tooltip wouldn't show).
          This caused a bug though, where an onClick was passed in with an event has the
          argument and this meant that clicking on the "x" (remove) button would trigger
          2 onClicks; one with the "name" and one with an event.
         */
        {...omit(otherProps, ['onClick'])}
      >
        {name}
        {editable ? <div className="pill__remove" onClick={this.handleClick}>x</div> : ''}
      </div>
    );
  }
}

const LocationThumbnail = ({ geopoint, width, height, className }) => {
  let node = <noscript />;
  if (geopoint && geopoint.lat && geopoint.lng) {
    const geoString = `${geopoint.lat},${geopoint.lng}`;

    node = (
      <div className={`location-thumbnail ${className}`}>
        <img
          className="location-thumbnail__map"
          alt=""
          src={'https://maps.googleapis.com/maps/api/staticmap' +
               '?key=AIzaSyDQSxBVPFJlO3sb0MihxhDMU0yH2uIV5c8' +
               `&markers=${geoString}&center=${geoString}&size=${width}x${height}&zoom=15`}
        />
      </div>
    );
  }

  return node;
};

LocationThumbnail.propTypes = {
  geopoint: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
};

LocationThumbnail.defaultProps = {
  width: 500,
  height: 125,
  className: '',
};


export default class LocationPicker extends React.Component {
  static propTypes = {
    // This prop will be run everytime a user selects an address from the autocomplete dropdown
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onDropdownSelect: PropTypes.func,
    className: PropTypes.string,
    style: PropTypes.shape({}),
    inputId: PropTypes.string,
    hideAddress: PropTypes.bool,
    location: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        address: PropTypes.string,
        locality: PropTypes.object,
        region: PropTypes.object,
        country: PropTypes.object,
      }),
    ]),
    showMap: PropTypes.bool,
    mapWidth: PropTypes.number,
    mapHeight: PropTypes.number,
    placeholder: PropTypes.string,
    clickToClear: PropTypes.bool,
    shouldAutoFocus: PropTypes.bool,
  }

  static defaultProps = {
    hideAddress: true,
    showMap: false,
    mapWidth: LocationThumbnail.defaultProps.width,
    mapHeight: LocationThumbnail.defaultProps.height,
    placeholder: 'Enter suburb',
    inputId: 'location-picker',
  }

  constructor(props) {
    super(props);

    this.autocomplete = {};

    this.state = {
      inputText: '',
    };

    this.geolocate = this.geolocate.bind(this);
    this.clearAddress = this.setAddress.bind(this, null);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    const options = {
      bounds: new google.maps.LatLngBounds(
        new google.maps.LatLng(-44, 113),
        new google.maps.LatLng(-10, 154)
      ),
    };

    this.autocomplete = new google.maps.places.Autocomplete(this.autocompleteRef, options);
    google.maps.event.addListener(this.autocomplete, 'place_changed', () => {
      this.fillInAddress();
      if (this.props.onDropdownSelect) this.props.onDropdownSelect();
    });
    // eslint-disable-next-line react/no-find-dom-node
    google.maps.event.addDomListener(ReactDOM.findDOMNode(this.autocompleteRef), 'keydown', (e) => {
      // We don't want form submits to trigger until a location (empty or valid) is selected
      if (e.keyCode === 13 && this.state.inputText) {
        e.preventDefault();
      }
    });
  }

  get address() {
    return readableLocation(this.props.location);
  }

  setAddress(location, event) {
    if (this.props.hideAddress && location) delete location.address;
    this.setState({
      inputText: get(event, 'target.value', ''),
    });
    const { onChange } = this.props;
    if (onChange) onChange(location);
  }

  fillInAddress= () => {
    const location = new LocationParser(this.autocomplete.getPlace()).parse();
    this.setAddress(location);
  }

  geolocate = () => {
    if (window.navigator.geolocation) {
      window.navigator.geolocation.getCurrentPosition((position) => {
        const geolocation = new google.maps.LatLng(
          position.coords.latitude,
          position.coords.longitude
        );
        this.autocomplete.setBounds(new google.maps.LatLngBounds(geolocation, geolocation));
      });
    }
  }

  handleKeyDown = (event) => {
    const deleteOrBackspacePressed = event.keyCode === 46 || event.keyCode === 8;
    if (this.props.clickToClear && deleteOrBackspacePressed && this.address) {
      this.clearAddress();
    }
  }

  render() {
    const { clickToClear, style, className, showMap, mapWidth,
      mapHeight, placeholder, inputId, shouldAutoFocus, onBlur } = this.props;

    let inputVal = this.address || this.state.inputText;
    if (clickToClear && this.address) inputVal = '';

    const placeholderText = (clickToClear && this.address) ? '' : placeholder;

    return (
      <div className="location-picker-wrapper">
        {
          clickToClear && this.address &&
            <Pill
              editable
              onClick={this.clearAddress}
              name={this.address}
            />
        }
        <input
          id={inputId}
          style={style}
          className={className}
          ref={(el) => { this.autocompleteRef = el; }}
          placeholder={placeholderText}
          onFocus={this.geolocate}
          onChange={this.clearAddress}
          onBlur={onBlur}
          type="text"
          value={inputVal}
          onKeyDown={this.handleKeyDown}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={shouldAutoFocus}
        />
        {showMap &&
          <LocationThumbnail
            width={mapWidth}
            height={mapHeight}
            geopoint={get(this.props, 'location.geo')}
          />
        }
      </div>
    );
  }
}
